import { CheckCircle2, CircleDot, Clock, Fingerprint, ShieldCheck } from "lucide-react";

const RISKS = [["Critical", 2, "bg-rose-400", 10], ["High", 5, "bg-amber-400", 24], ["Medium", 11, "bg-cyan-400", 52], ["Low", 21, "bg-emerald-400", 88]];
const TASKS = [["Publish updated privacy notice", "Due in 3 days", Clock, "text-amber-300"], ["Map vendor data flows", "In progress", CircleDot, "text-cyan-300"], ["Breach response runbook", "Done", CheckCircle2, "text-emerald-300"]];

function ScoreRing({ value }) {
  const r = 30, c = 2 * Math.PI * r;
  return (
    <div className="relative h-20 w-20 shrink-0">
      <svg viewBox="0 0 72 72" className="h-20 w-20 -rotate-90">
        <circle cx="36" cy="36" r={r} fill="none" stroke="rgba(148,163,184,.18)" strokeWidth="7" />
        <circle cx="36" cy="36" r={r} fill="none" stroke="url(#ring)" strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)} className="auth-ring" />
        <defs><linearGradient id="ring" x1="0" x2="1"><stop offset="0" stopColor="#22d3ee" /><stop offset="1" stopColor="#34d399" /></linearGradient></defs>
      </svg>
      <div className="absolute inset-0 grid place-items-center"><span className="font-display text-xl font-extrabold">{value}<span className="text-xs text-slate-400">%</span></span></div>
    </div>
  );
}

const Card = ({ title, children, className = "", testid }) => (
  <div data-testid={testid} className={`rounded-xl border border-white/[0.07] bg-white/[0.035] p-3.5 ${className}`}>
    <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{title}</p>{children}
  </div>
);

export function DashboardPreview() {
  return (
    <div data-testid="auth-dashboard-preview" aria-hidden="true" className="auth-float rounded-2xl border border-white/10 bg-[#0d1a3a]/80 p-4 shadow-[0_30px_80px_-20px_rgba(2,8,23,.8)] backdrop-blur-xl">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-white/15" /><span className="h-2.5 w-2.5 rounded-full bg-white/15" /><span className="h-2.5 w-2.5 rounded-full bg-white/15" /></div>
        <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-slate-400">Sample workspace</span>
      </div>
      <div className="grid grid-cols-5 gap-3">
        <Card title="DPDP compliance readiness" className="col-span-3" testid="preview-score">
          <div className="flex items-center gap-4"><ScoreRing value={72} />
            <div className="min-w-0 flex-1 space-y-2 text-[11px]">
              <div className="flex justify-between text-slate-300"><span>Controls assessed</span><span className="font-semibold text-cyan-300">29 / 36</span></div>
              <div className="h-1.5 rounded-full bg-white/10"><div className="auth-bar h-1.5 w-[81%] rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400" /></div>
              <p className="text-slate-400">7 gaps with evidence pending</p>
            </div>
          </div>
        </Card>
        <Card title="Consent status" className="col-span-2" testid="preview-consent">
          <div className="flex items-center gap-2"><Fingerprint className="h-5 w-5 text-teal-300" /><span className="font-display text-lg font-bold">4,812</span></div>
          <p className="mt-1 text-[11px] text-slate-400">active consents</p>
          <div className="mt-2 flex gap-1 text-[10px]"><span className="rounded bg-emerald-400/15 px-1.5 py-0.5 text-emerald-300">96% valid</span><span className="rounded bg-amber-400/15 px-1.5 py-0.5 text-amber-300">38 renew</span></div>
        </Card>
        <Card title="Open compliance tasks" className="col-span-3" testid="preview-tasks">
          <ul className="space-y-2">{TASKS.map(([t, s, I, c]) => (
            <li key={t} className="flex items-center gap-2 text-[11px]"><I className={`h-3.5 w-3.5 shrink-0 ${c}`} /><span className="truncate text-slate-200">{t}</span><span className="ml-auto shrink-0 text-slate-500">{s}</span></li>
          ))}</ul>
        </Card>
        <Card title="Risk overview" className="col-span-2" testid="preview-risk">
          <div className="space-y-1.5">{RISKS.map(([l, n, c, w]) => (
            <div key={l} className="flex items-center gap-2 text-[10px]"><span className="w-11 text-slate-400">{l}</span>
              <div className="h-1.5 flex-1 rounded-full bg-white/10"><div className={`auth-bar h-1.5 rounded-full ${c}`} style={{ width: `${w}%` }} /></div><span className="w-4 text-right text-slate-300">{n}</span></div>
          ))}</div>
        </Card>
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-teal-400/15 bg-teal-400/[0.06] px-3 py-2 text-[11px] text-teal-100">
        <ShieldCheck className="h-3.5 w-3.5 text-teal-300" />AI found 3 new gaps in your vendor contracts and drafted fixes.
      </div>
    </div>
  );
}
